// Axis maths for the scrubber — kept out of the component so the component file
// exports only components (Fast Refresh + the project's module cohesion rule).
//
// The axis is the model's own marker list, in order. It is a SEQUENCE of stops,
// not a continuum: stops are evenly spaced and labelled with the author's own
// token (`v3`, `2026-Q3`, `1.5.2`), because nothing maps a marker onto a date it
// did not name. `now` is one more stop, sitting where the past meets the future.

import { labelAt, type ScrubPoint, type Timeline } from "@archlang/engine";

export interface ScrubStop {
  /** Stable key for React and test ids. */
  readonly key: string;
  /** The author's marker text; absent on the `now` stop. */
  readonly marker?: string;
  /** The point this stop selects — what the store holds when it is picked. */
  readonly point: ScrubPoint;
  /** Ordinal on the model timeline, for laying the stops out in order. */
  readonly at: number;
  /**
   * Position along the rendered axis, 0..1.
   *
   * The denominator is the STOP COUNT, not the last index, because the stops
   * share the track with the parked "All" detent one step past the last of them.
   * Dividing by the last index instead spread the ticks over the whole track
   * while the handle only reached `stops.length - 1` of it, so every tick drifted
   * right of the position that selects it — by a whole step at the far end.
   */
  readonly frac: number;
}

/**
 * Every stop the scrubber can land on: `now`, then one per marker, in timeline
 * order. Empty when the model names no markers at all — a model whose only
 * lifecycle decorations are bare (`@deprecated` with no marker) has states to
 * paint but no axis to drag along.
 */
export function scrubStops(timeline: Timeline | undefined): readonly ScrubStop[] {
  if (!timeline || timeline.markers.length === 0) return [];
  const ordered: Omit<ScrubStop, "frac">[] = timeline.markers.map((m, i) => ({
    key: m,
    marker: m,
    point: { kind: "marker", text: m },
    at: i,
  }));
  // `now` is a half-integer, so it slots between markers without ever colliding
  // with one. Insert it at its ordinal rather than pinning it to an end.
  const nowIdx = ordered.findIndex((s) => s.at > timeline.now);
  const now: Omit<ScrubStop, "frac"> = { key: " now", point: { kind: "now" }, at: timeline.now };
  ordered.splice(nowIdx === -1 ? ordered.length : nowIdx, 0, now);

  return ordered.map((s, i) => ({ ...s, frac: i / ordered.length }));
}

/** The label for a scrub ordinal: the author's marker when the point sits on
 *  one, else undefined — the caller renders its own "now" / between-stops text. */
export function markerAt(timeline: Timeline | undefined, at: number): string | undefined {
  return timeline ? labelAt(timeline, at) : undefined;
}

/**
 * Index of the stop a point sits on, for the range input. -1 when the point
 * names a marker this axis no longer has.
 *
 * Matched by NAME rather than by ordinal, because an ordinal is an index into an
 * axis the author is editing: add a marker and every later index shifts, so a
 * stored ordinal starts naming a different stop than the one that was clicked.
 */
export function stopIndexOf(stops: readonly ScrubStop[], point: ScrubPoint | null): number {
  if (point === null) return -1;
  return stops.findIndex((s) =>
    point.kind === "now" ? s.marker === undefined : s.marker === point.text,
  );
}
